// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import type { TurnResponseDto } from '../api/client.js';
import { BilalMark } from './BilalMark.js';
import { ListenButton, type SpeechControl } from './ListenButton.js';
import { SourceCards } from './SourceCards.js';
import { messages, useMessages, type Language } from '../i18n.js';

/**
 * What this turn should say, taken from the server and never invented here.
 *
 * There used to be a table of stand-ins in this file, and what it put on screen for a crisis turn
 * was an internal code plus a sentence about our own review process — shown to someone who may
 * have just said they are in danger. The server decides this now (`delivery`: `notice`), so every
 * client says the same thing: reviewed wording when it exists, the interim line while it does not,
 * and a code never.
 */
/** The card's eyebrow, in the dual for two and the plural for three (redesign canvas). */
function questionLabel(count: number, t: Words): string {
  if (count === 1) return t.questionOne;
  if (count === 2) return t.questionTwo;
  return t.questionMany;
}

export function safetyText(turn: TurnResponseDto): string {
  const notice = turn.notice;
  if (notice === null) return '';
  return (notice.reviewPending ? notice.interimText : notice.text) ?? '';
}

/**
 * Degrade codes that mean the *service* could not answer right now — nothing about the person's
 * words was refused. These read as outages with a next step; every other code keeps the reviewed
 * fallback wording above.
 */
/**
 * The operational codes that mean *try again* — the service could not answer, and nothing about the
 * person's words was refused. The application retries these for them rather than asking them to
 * retype: a person who wrote something hard once should not have to write it twice because a
 * provider was busy.
 *
 * `religious_leakage_blocked` is deliberately absent. It is about what was written, not about an
 * outage, and re-sending the same words would only produce it again.
 *
 * `circuit_open` is absent for the opposite reason: the breaker exists to stop calls to a provider
 * that is failing, and a client that retried through it would be defeating the one mechanism
 * protecting the next person's turn.
 */
export const RETRYABLE_DEGRADES: ReadonlySet<string> = new Set([
  'composer_failed',
  'guard_unavailable',
]);

/**
 * What a person is told when a turn did not answer normally. Operational only: the three safety
 * actions carry no wording here and never will — that text is the clinician's (NA-05, NA-05c).
 */
export const OPERATIONAL_MESSAGES = messages({
  ar: {
    composer_failed: 'الخدمة غير متاحة مؤقتًا. أعد المحاولة بعد قليل — رسالتك لم تُرفض.',
    religious_leakage_blocked:
      'تعذّرت صياغة هذا الرد وفق ضوابط المحتوى الديني — رسالتك لم تُرفض، أعد إرسالها أو صِغها بشكل آخر.',
    circuit_open: 'الخدمة مشغولة الآن. انتظر لحظات ثم أعد المحاولة.',
    guard_unavailable: 'الخدمة غير متاحة مؤقتًا. أعد المحاولة بعد قليل.',
    question_budget_exhausted: 'اكتملت أسئلة هذه الجلسة. ابدأ محادثة جديدة للمتابعة.',
  },
  en: {
    composer_failed:
      'The service is briefly unavailable. Try again in a moment — your message was not refused.',
    religious_leakage_blocked:
      'This reply could not be composed within the religious-content rules — your message was not refused; send it again or put it differently.',
    circuit_open: 'The service is busy right now. Wait a moment, then try again.',
    guard_unavailable: 'The service is briefly unavailable. Try again in a moment.',
    question_budget_exhausted:
      'This conversation has used its questions. Start a new one to carry on.',
  },
});

/** The words for a degrade code, in the language in force; empty when the code has none. */
export function operationalMessage(code: string, language: Language): string {
  const words: Readonly<Record<string, string>> = OPERATIONAL_MESSAGES[language];
  return words[code] ?? '';
}

const T = messages({
  ar: {
    reply: 'رد بلال',
    questionOne: 'سؤال من بلال',
    questionTwo: 'سؤالان من بلال',
    questionMany: 'أسئلة من بلال',
    questionBadge: '؟',
    numerals: ['١', '٢', '٣'],
  },
  en: {
    reply: 'Bilal’s reply',
    questionOne: 'A question from Bilal',
    questionTwo: 'Two questions from Bilal',
    questionMany: 'Questions from Bilal',
    questionBadge: '?',
    numerals: ['1', '2', '3'],
  },
});

type Words = (typeof T)['ar'];

export function AssistantResponse({
  turn,
  speech,
  entrance,
  speakingLevel,
}: {
  readonly turn: TurnResponseDto;
  /** Passed through to the cards: the interface says what the system reports, never what it assumes. */
  /**
   * How to ask the server to speak this turn, or null when there is nothing to name. Deterministic
   * safety wording never reaches it: that branch returns before the control is rendered at all.
   */
  readonly speech: SpeechControl | null;
  /**
   * True only when this reply has just arrived verified. The paragraphs rise in one after another —
   * a reveal of finished words, never a typewriter pretending the answer is still being written.
   */
  readonly entrance: boolean;
  /** Real playback loudness while this turn is being spoken, or null when it is not. */
  readonly speakingLevel: number | null;
}): React.JSX.Element {
  const t = useMessages(T);
  if (turn.safetyAction !== 'none') {
    // The server decides what a person is told (delivery: `notice`), so every client says the same
    // thing. Reviewed wording reads as an outage line; an interim one is still a safety state,
    // because it stands in for the clinician's text rather than replacing it.
    const reviewed = turn.notice?.reviewPending === false ? turn.notice.text : null;
    return (
      <div role="status" className={reviewed === null ? 'safety-state' : 'outage-state'}>
        {safetyText(turn)}
      </div>
    );
  }
  // On an ordinary reply a notice is an advisory (ADR-044); the safety states returned above.
  // Read as possibly absent: this is network JSON, and an older payload without the field is no
  // advisory rather than a broken reply.
  const notice: unknown = turn.notice;
  const advisory = typeof notice === 'object' && notice !== null ? safetyText(turn) : '';
  const paragraphs = (text: string): readonly string[] =>
    text
      .split(/\n{2,}/)
      .map((block) => block.trim())
      .filter((block) => block.length > 0);
  // The question is the server's to lift out (delivery: `question`); this only avoids saying it
  // twice. The prose stays whole in the data, so what is spoken and stored is unchanged.
  const questions: readonly string[] =
    turn.replyKind === 'question'
      ? turn.questions !== undefined && turn.questions.length > 0
        ? turn.questions
        : typeof turn.question === 'string'
          ? [turn.question]
          : []
      : [];
  // The trailing run is peeled off the end, last question first, so the body never repeats it.
  let bodyText = turn.assistantProse.trimEnd();
  for (const asked of [...questions].reverse())
    if (bodyText.endsWith(asked)) bodyText = bodyText.slice(0, -asked.length).trimEnd();
  const blocks = paragraphs(bodyText);
  // Tolerant of older stored operations that predate the field.
  const linkBlocks =
    typeof turn.sourceLinkProse === 'string' ? paragraphs(turn.sourceLinkProse) : [];
  const delayAfterProse = blocks.length * 140 + 60;
  return (
    <>
      <article
        aria-label={t.reply}
        className={entrance ? 'assistant-prose reveal' : 'assistant-prose'}
      >
        {blocks.map((block, index) => (
          <p
            key={index}
            className="prose-block"
            dir="auto"
            style={entrance ? { animationDelay: `${String(index * 140)}ms` } : undefined}
          >
            {block}
          </p>
        ))}
        {/* The founder's flow: the verified source is part of the talk — provider text in its own
            distinct block between the words and their link, never blended into the prose string. */}
        {turn.sourceCards.length > 0 && (
          <div
            className={entrance ? 'flow-source reveal-late' : 'flow-source'}
            style={entrance ? { animationDelay: `${String(delayAfterProse)}ms` } : undefined}
          >
            <SourceCards cards={turn.sourceCards} />
          </div>
        )}
        {/* The question card is a labelled section (role: region). An aside nested in an article
            has no landmark role under the HTML-AAM, so it would be unreachable by role. */}
        {questions.length > 0 && (
          <section
            className={entrance ? 'question-card reveal-late' : 'question-card'}
            aria-label={questionLabel(questions.length, t)}
            style={entrance ? { animationDelay: `${String(delayAfterProse)}ms` } : undefined}
          >
            <p className="question-card-label">{questionLabel(questions.length, t)}</p>
            {questions.length === 1 ? (
              <p className="question-card-text" dir="auto">
                {/* The badge carries its own `dir` so it cannot decide the paragraph's. In Arabic
                    it is ؟, which the bidi algorithm classes as a strong right-to-left character,
                    and it sits first: without this the card would run right-to-left even around an
                    English question, putting the question mark on the wrong side. An element with
                    its own direction is skipped when `dir="auto"` looks for the first strong
                    character. */}
                <span className="question-badge" aria-hidden="true" dir="ltr">
                  {t.questionBadge}
                </span>
                {questions[0]}
              </p>
            ) : (
              <ol className="question-card-list">
                {questions.map((asked, index) => (
                  <li key={asked} dir="auto">
                    <span className="question-number" aria-hidden="true">
                      {t.numerals[index] ?? String(index + 1)}
                    </span>
                    <span className="question-card-text">{asked}</span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        )}
        {linkBlocks.map((block, index) => (
          <p
            key={`link-${String(index)}`}
            className="prose-block source-link-prose"
            dir="auto"
            style={
              entrance
                ? { animationDelay: `${String(delayAfterProse + 200 + index * 140)}ms` }
                : undefined
            }
          >
            {block}
          </p>
        ))}
        {/* ADR-044: the server's fixed line pointing to someone qualified, after the reply and its
            link, never instead of either. The words are delivery's; this only places them. */}
        {advisory !== '' && (
          <p role="note" className="advisory-note" dir="auto">
            {advisory}
          </p>
        )}
      </article>
      <div
        className={entrance ? 'after-prose reveal-late' : 'after-prose'}
        style={
          entrance
            ? { animationDelay: `${String(delayAfterProse + 260 + linkBlocks.length * 140)}ms` }
            : undefined
        }
      >
        {speakingLevel !== null && (
          <span className="speaking-presence" aria-hidden="true">
            <BilalMark state="speaking" depth="balanced" size={22} amplitude={speakingLevel} />
          </span>
        )}
        {speech !== null && <ListenButton {...speech} />}
      </div>
    </>
  );
}
